import React from 'react';
import { ProductStatus } from '../../../types/Products/product';
import clsx from 'clsx';
import { PenBox, Trash2 } from 'lucide-react';
import { PRODUCTS_QUANTITY } from '../../../Constants/productConstants';
import { ProductInventoryType } from '../../../types/inventory/ProductInventoryType';
import { images, IonicButton } from 'ionic-ui';
import useProductCardActions from '../../../hooks/useProductCardActions';
import EmptyPage from '../../ui/EmptyPage';
import AddNewProductDialog from '../catalog/AddNewProductDialog/AddNewProductDialog';
import { useDialog } from '../../../context/Dialog';
import { formatPrice } from '../../../utils/formatPrice';
import Pagination from '../Paginator';

type Props = {
  tableData: Array<ProductInventoryType>;
  totalPages?: number;
};

const statusTheme = (status: ProductStatus) =>
  clsx(
    status === 'in stock' && 'text-green-600',
    status === 'low stock' && 'text-yellow-600',
    status === 'out of stock' && 'text-red-600'
  );

const ProductAvailabilityTag = ({
  status = 'out of stock',
}: {
  status: ProductStatus;
}) => {
  const tagStyle = clsx('text-xs block', statusTheme(status));
  return (
    <>
      {status === 'in stock' && <small className={tagStyle}>✓ In Stock</small>}
      {status === 'out of stock' && (
        <small className={tagStyle}>❌ Out of stock</small>
      )}
      {status === 'low stock' && (
        <small className={tagStyle}>⚠️ Low stock</small>
      )}
    </>
  );
};

const InventoryRow = ({
  product_id: id,
  category,
  price,
  product_name: name,
  stock_left: quantity,
  units_sold: quantity_sold,
  images: product_images,
}: Partial<ProductInventoryType>) => {
  const status: ProductStatus = React.useMemo(() => {
    if (!quantity || quantity === PRODUCTS_QUANTITY.OUT_OF_STOCK)
      return 'out of stock';
    else if (quantity <= PRODUCTS_QUANTITY.LOW_ON_STOCK) return 'low stock';
    return 'in stock';
  }, [quantity]);

  const categoryStyle = clsx(
    'px-2 py-1 rounded-md text-xs font-normal text-nowrap',
    status !== 'out of stock'
      ? 'high-emphasis-text border border-customs-light-cyan bg-customs-light-cyan-foreground'
      : 'low-emphasis-text border-gray-50 bg-gray-50'
  );

  const stockColStyle = statusTheme(status);
  const { onRestock, onRemove } = useProductCardActions({
    id,
    productName: name,
  });

  return (
    <tr className="high-emphasis-text font-medium text-sm padded-row">
      <td>
        <div className="flex gap-2 w-fit items-center justify-start">
          <img
            src={product_images?.[0]?.file_url || images.placeholder}
            alt={name}
            className="w-8 h-8 rounded-sm object-cover object-center"
          />
          <div className="block  flex-1 truncate space-y-1">
            <span className="block  text-nowrap truncate" title={name}>
              {name}
            </span>
            <ProductAvailabilityTag status={status} />
          </div>
        </div>
      </td>
      <td>
        <span className={categoryStyle}>{category}</span>
      </td>
      <td>{quantity_sold}</td>
      <td className={stockColStyle}>{quantity}</td>
      <td className="font-bold text-primary">
        {formatPrice(Number(price) * (quantity_sold ?? 0))}
      </td>
      <td>
        <div className="high-emphasis-text font-medium flex items-center gap-2">
          <button
            type="button"
            onClick={() => onRestock()}
            className="flex p-2 gap-2 justify-center rounded-sm items-center bg-customs-light-blue-foreground"
          >
            <PenBox className="size-4" />
            <span>Edit</span>
          </button>
          <button
            onClick={() => onRemove()}
            type="button"
            className="p-2.5 sm:p-3 rounded-sm bg-gray-50 hover:bg-gray-100"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default function InventoryTable({ tableData, totalPages }: Props) {
  const { openDialog } = useDialog();

  const handleNewProduct = () => {
    openDialog(<AddNewProductDialog />, { allowBackgroundDismiss: false });
  };

  if (tableData.length < 1) {
    return (
      <EmptyPage
        message="No Products in catalog"
        className="sm:col-span-2 md:col-span-3 "
      >
        <IonicButton onClick={handleNewProduct} variant="ghost" size="lg">
          Add New Product
        </IonicButton>
      </EmptyPage>
    );
  }

  return (
    <div className="p-0 sm:p-4 sm:rounded-lg sm:bg-gray-50 space-y-4">
      <h6 className="high-emphasis-text font-semibold">Product Performance</h6>

      {/* DATA TABLE */}
      <div className="w-full dashboard-scroller overflow-x-auto">
        <table className="bg-white rounded-md w-full border-collapse">
          <thead>
            <tr className="medium-emphasis-text text-sm border-b border-border text-nowrap padded-row">
              <td className="min-w-fit">Product</td>
              <td className="min-w-fit">Category</td>
              <td className="min-w-fit">Units Sold</td>
              <td className="min-w-fit">Stock Left</td>
              <td className="min-w-fit">Earnings</td>
              <td className="min-w-fit">Action</td>
            </tr>
          </thead>
          <tbody>
            {tableData.map((product, index) => (
              <InventoryRow {...product} key={index} />
            ))}
          </tbody>
        </table>
      </div>
      {totalPages && (
        <div className="w-fit ml-auto">
          <Pagination onPageChange={() => {}} totalPages={totalPages} />
        </div>
      )}
    </div>
  );
}

export function InventoryTableSkeleton() {
  const dummyRows = new Array(8).fill(0);
  return (
    <div className="p-0 sm:p-4 sm:rounded-lg sm:bg-gray-50 space-y-4">
      <span className="block h-10 w-full rounded-sm sm:w-1/3 bg-gray-100 animate-pulse" />

      {/* DATA TABLE */}
      <div className="w-full dashboard-scroller overflow-x-auto">
        <div className="bg-white rounded-md w-full border-collapse space-y-2 p-4 sm:p-6">
          {dummyRows.map((_, idx) => (
            <span
              className="block h-10 bg-gray-100 rounded-sm animate-pulse"
              key={idx}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
